/**
 * Yassine Advisor — ChatGPT perfume guide over the live catalog.
 */
(() => {
  const CATALOG_COLLECTIONS = [
    "women", "men", "unisex-perfumes", "kids", "kids-unisex-perfumes",
    "gift-sets", "bundles", "best-seller", "our-favourite", "new-arrival-1",
    "lattafa-perfumes", "lattafa-pride", "french-avenue", "maison-alhambra",
    "ard-al-zaafaran", "fragrance-world", "give-me-gourmand", "rasasi",
    "nusuk", "riiffs", "parisis", "reef", "orientica",
    "tubbees", "hersh", "junaid", "disney", "new-arrival-2",
    "lighters", "zippo-lighters",
  ];

  const LIGHTER_HANDLES = new Set(["lighters", "zippo-lighters"]);

  const AUDIENCE = {
    women: { handles: ["women"] },
    men: { handles: ["men"] },
    unisex: { handles: ["unisex-perfumes"] },
    kids: { handles: ["kids", "kids-unisex-perfumes", "disney", "tubbees"] },
    gift: { handles: ["gift-sets", "bundles", "best-seller", "our-favourite", "lighters", "zippo-lighters"] },
  };

  const CATEGORIES = {
    perfume: { label: "Perfume", handles: null },
    lighter: { label: "Lighter", handles: ["lighters", "zippo-lighters"] },
  };

  const VIBES = {
    fresh: ["fresh", "citrus", "bergamot", "lemon", "aqua", "sport", "clean", "ocean", "breeze", "cool", "ice", "hawas"],
    floral: ["floral", "rose", "jasmine", "bloom", "flower", "yara", "pink", "peony", "orchid", "violet"],
    sweet: ["sweet", "caramel", "gourmand", "sugar", "khamrah", "angham", "chocolate", "honey", "candy", "vanilla"],
    oriental: ["oud", "amber", "oriental", "musk", "incense", "bakhoor", "arabian", "resin", "myrrh"],
    woody: ["woody", "wood", "spice", "spicy", "pepper", "cedar", "tobacco", "leather", "sandalwood", "vetiver"],
  };

  // Specific notes shoppers ask for — must appear in catalog text before we recommend.
  const NOTE_ALIASES = {
    vanilla: ["vanilla", "vanille"],
    rose: ["rose", "roses"],
    oud: ["oud", "oudh", "agarwood"],
    jasmine: ["jasmine", "jasmin"],
    amber: ["amber"],
    musk: ["musk"],
    citrus: ["citrus", "bergamot", "lemon", "orange", "grapefruit"],
    caramel: ["caramel"],
    pistachio: ["pistachio", "pistache"],
    chocolate: ["chocolate", "cacao", "cocoa"],
    sandalwood: ["sandalwood", "sandal"],
    leather: ["leather"],
    tobacco: ["tobacco"],
    lavender: ["lavender"],
    coconut: ["coconut"],
    coffee: ["coffee"],
    honey: ["honey"],
    saffron: ["saffron"],
  };

  const BRANDS = {
    lattafa: ["lattafa-perfumes", "lattafa-pride"],
    "french avenue": ["french-avenue"],
    "maison alhambra": ["maison-alhambra"],
    "ard al zaafaran": ["ard-al-zaafaran"],
    rasasi: ["rasasi"],
    orientica: ["orientica"],
    "fragrance world": ["fragrance-world"],
    nusuk: ["nusuk"],
    disney: ["disney"],
  };

  const STARTERS = [
    "I need a perfume",
    "Looking for a gift",
    "I want a lighter",
    "Something under $70",
  ];

  const cleanText = (value = "") =>
    String(value)
      .replace(/\s*[—–]\s*/g, ", ")
      .replace(/\s+-\s+/g, ", ")
      .replace(/\s*,\s*,+/g, ", ")
      .replace(/\s{2,}/g, " ")
      .trim();

  const STORAGE_KEY = "yassine-perfume-advisor-chat-v1";
  const HISTORY_KEY = "yassine-perfume-advisor-history-v1";
  const STORAGE_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 30; // 30 days
  const HISTORY_MAX = 20;
  const PRODUCT_PAGE_SIZE = 4;
  const PRODUCT_POOL_SIZE = 12;

  const makeId = () =>
    `chat_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

  const stripHtml = (html = "") =>
    String(html)
      .replace(/<script[\s\S]*?<\/script>/gi, " ")
      .replace(/<style[\s\S]*?<\/style>/gi, " ")
      .replace(/<[^>]+>/g, " ")
      .replace(/&nbsp;/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  class PerfumeAdvisor extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
      }

      this.panel = this.querySelector("[data-pa-panel]");
      this.messages = this.querySelector("[data-pa-messages]");
      this.form = this.querySelector("[data-pa-form]");
      this.input = this.querySelector("[data-pa-input]");
      this.catalog = [];
      this.catalogReady = null;
      this.chatMessages = [];
      this.prefs = {};
      this.transcript = [];
      this.moreQueue = [];
      this.chatId = makeId();
      this.busy = false;
      this.historyPanel = this.querySelector("[data-pa-history-panel]");
      this.historyList = this.querySelector("[data-pa-history-list]");
      this.historyBtn = this.querySelector("[data-pa-history]");
      this.aiProxyUrl = (this.dataset.aiProxyUrl || "").trim()
        || "https://yassine-perfume-advisor.yassinegrouplb-chat.workers.dev";
      this.useAi = this.dataset.useAi !== "false" && Boolean(this.aiProxyUrl);

      this.querySelector("[data-pa-open]")?.addEventListener("click", () => this.open());
      this.querySelector("[data-pa-close]")?.addEventListener("click", () => this.close());
      this.querySelector("[data-pa-restart]")?.addEventListener("click", () => this.restart());
      this.historyBtn?.addEventListener("click", () => this.toggleHistory());
      this.querySelector("[data-pa-history-close]")?.addEventListener("click", () => this.hideHistory());
      this.form?.addEventListener("submit", (event) => {
        event.preventDefault();
        const value = this.input.value.trim();
        if (!value || this.busy) return;
        this.input.value = "";
        this.send(value);
      });

      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && this.classList.contains("is-open")) {
          if (!this.historyPanel?.hidden) {
            this.hideHistory();
            return;
          }
          this.close();
        }
      });

      this.migrateLegacyChat();
      this.catalogReady = this.loadCatalog();
    }

    open() {
      this.classList.add("is-open");
      this.setAttribute("aria-expanded", "true");
      if (!this.messages.childElementCount) {
        if (!this.restoreActiveChat()) this.start();
      }
      this.input?.focus({ preventScroll: true });
    }

    close() {
      this.hideHistory();
      this.classList.remove("is-open");
      this.setAttribute("aria-expanded", "false");
      this.saveChat();
    }

    restart() {
      this.saveChat();
      this.chatId = makeId();
      this.chatMessages = [];
      this.prefs = {};
      this.transcript = [];
      this.moreQueue = [];
      this.busy = false;
      this.messages.innerHTML = "";
      this.hideHistory();
      this.setActiveChatId(this.chatId);
      this.start();
    }

    toggleHistory() {
      if (!this.historyPanel) return;
      if (this.historyPanel.hidden) this.showHistory();
      else this.hideHistory();
    }

    showHistory() {
      if (!this.historyPanel) return;
      this.renderHistoryList();
      this.historyPanel.hidden = false;
      this.historyBtn?.setAttribute("aria-expanded", "true");
      this.classList.add("is-history-open");
    }

    hideHistory() {
      if (!this.historyPanel) return;
      this.historyPanel.hidden = true;
      this.historyBtn?.setAttribute("aria-expanded", "false");
      this.classList.remove("is-history-open");
    }

    splitProductPages(products = []) {
      const pool = products.slice(0, PRODUCT_POOL_SIZE);
      return {
        shown: pool.slice(0, PRODUCT_PAGE_SIZE),
        more: pool.slice(PRODUCT_PAGE_SIZE),
      };
    }

    storageAvailable() {
      try {
        const key = "__pa_test__";
        window.localStorage.setItem(key, "1");
        window.localStorage.removeItem(key);
        return true;
      } catch {
        return false;
      }
    }

    chatTitleFromTranscript(transcript = []) {
      const firstUser = transcript.find((e) => e?.role === "user" && e.text);
      if (firstUser?.text) {
        const t = cleanText(firstUser.text);
        return t.length > 42 ? `${t.slice(0, 42)}…` : t;
      }
      return "New chat";
    }

    readHistoryStore() {
      if (!this.storageAvailable()) return { activeId: null, chats: [] };
      try {
        const raw = window.localStorage.getItem(HISTORY_KEY);
        if (!raw) return { activeId: null, chats: [] };
        const data = JSON.parse(raw);
        const chats = Array.isArray(data.chats)
          ? data.chats.filter((c) => c && c.id && Array.isArray(c.transcript) && c.transcript.length)
          : [];
        const now = Date.now();
        const fresh = chats.filter((c) => !c.updatedAt || now - c.updatedAt <= STORAGE_MAX_AGE_MS);
        return {
          activeId: data.activeId || null,
          chats: fresh.slice(0, HISTORY_MAX),
        };
      } catch {
        return { activeId: null, chats: [] };
      }
    }

    writeHistoryStore(store) {
      if (!this.storageAvailable()) return;
      try {
        window.localStorage.setItem(
          HISTORY_KEY,
          JSON.stringify({
            activeId: store.activeId || null,
            chats: (store.chats || []).slice(0, HISTORY_MAX),
          })
        );
      } catch {
        /* ignore */
      }
    }

    setActiveChatId(id) {
      const store = this.readHistoryStore();
      store.activeId = id;
      this.writeHistoryStore(store);
    }

    migrateLegacyChat() {
      if (!this.storageAvailable()) return;
      try {
        const legacy = window.localStorage.getItem(STORAGE_KEY);
        if (!legacy) return;
        const data = JSON.parse(legacy);
        if (data && Array.isArray(data.transcript) && data.transcript.length) {
          const store = this.readHistoryStore();
          if (!store.chats.length) {
            const id = makeId();
            store.chats = [
              {
                id,
                title: this.chatTitleFromTranscript(data.transcript),
                updatedAt: data.savedAt || Date.now(),
                chatMessages: data.chatMessages || [],
                prefs: data.prefs || {},
                transcript: data.transcript,
              },
            ];
            store.activeId = id;
            this.writeHistoryStore(store);
          }
        }
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
    }

    saveChat() {
      if (!this.storageAvailable()) return;
      if (!this.transcript.length && !this.chatMessages.length) return;
      if (!this.chatId) this.chatId = makeId();

      const entry = {
        id: this.chatId,
        title: this.chatTitleFromTranscript(this.transcript),
        updatedAt: Date.now(),
        chatMessages: this.chatMessages.slice(-24),
        prefs: this.prefs || {},
        transcript: this.transcript.slice(-40),
      };

      const store = this.readHistoryStore();
      const idx = store.chats.findIndex((c) => c.id === entry.id);
      if (idx >= 0) store.chats[idx] = entry;
      else store.chats.unshift(entry);

      store.chats.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
      store.chats = store.chats.slice(0, HISTORY_MAX);
      store.activeId = entry.id;
      this.writeHistoryStore(store);
    }

    restoreActiveChat() {
      const store = this.readHistoryStore();
      if (!store.chats.length) return false;
      const active =
        store.chats.find((c) => c.id === store.activeId) || store.chats[0];
      if (!active) return false;
      return this.loadChatById(active.id, { welcomeNote: true });
    }

    loadChatById(id, options = {}) {
      const { welcomeNote = false } = options;
      const store = this.readHistoryStore();
      const data = store.chats.find((c) => c.id === id);
      if (!data) return false;

      this.chatId = data.id;
      this.chatMessages = Array.isArray(data.chatMessages) ? data.chatMessages : [];
      this.prefs = data.prefs && typeof data.prefs === "object" ? data.prefs : {};
      this.transcript = [];
      this.moreQueue = [];
      this.messages.innerHTML = "";
      this.setActiveChatId(data.id);

      const rebuilt = [];
      data.transcript.forEach((entry, index) => {
        if (!entry || !entry.role) return;
        if (entry.role === "user") {
          this.addUser(entry.text || "", { persist: false });
          rebuilt.push(entry);
          return;
        }
        if (entry.role === "bot") {
          let text = entry.text || "";
          // Replace old Lebanese welcome with English.
          if (index === 0 || this.isLegacyWelcome(text)) {
            if (this.isLegacyWelcome(text)) text = this.welcomeText();
          }
          const choices = Array.isArray(entry.choices)
            ? entry.choices.map((label, i) => ({ id: `saved-${i}`, label }))
            : null;
          const products = Array.isArray(entry.products) ? entry.products : null;
          const showStarters = index === 0 && !data.transcript.some((e) => e?.role === "user");
          const bubble = this.addBot(text, choices, products, {
            persist: false,
            showStarters: false,
            detail: Boolean(entry.detail),
          });
          if (showStarters) this.renderStarters(bubble);
          rebuilt.push({ ...entry, text });
        }
      });

      this.transcript = rebuilt.slice(-40);
      this.saveChat();

      if (welcomeNote) {
        const note = document.createElement("p");
        note.className = "perfume-advisor__resume-note";
        note.textContent = "Welcome back, your chat was saved on this device.";
        this.messages.appendChild(note);
      }

      this.scrollToBottom();
      return true;
    }

    deleteChatById(id) {
      const store = this.readHistoryStore();
      store.chats = store.chats.filter((c) => c.id !== id);
      if (store.activeId === id) store.activeId = store.chats[0]?.id || null;
      this.writeHistoryStore(store);

      if (this.chatId === id) {
        if (store.chats[0]) {
          this.loadChatById(store.chats[0].id);
        } else {
          this.chatId = makeId();
          this.chatMessages = [];
          this.prefs = {};
          this.transcript = [];
          this.moreQueue = [];
          this.messages.innerHTML = "";
          this.setActiveChatId(this.chatId);
          this.start();
        }
      }
      this.renderHistoryList();
    }

    formatHistoryDate(ts) {
      if (!ts) return "";
      try {
        return new Intl.DateTimeFormat(undefined, {
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }).format(new Date(ts));
      } catch {
        return "";
      }
    }

    renderHistoryList() {
      if (!this.historyList) return;
      const store = this.readHistoryStore();
      this.historyList.innerHTML = "";

      if (!store.chats.length) {
        const empty = document.createElement("p");
        empty.className = "perfume-advisor__history-empty";
        empty.textContent = "No old chats yet. Start chatting and they will appear here.";
        this.historyList.appendChild(empty);
        return;
      }

      store.chats.forEach((chat) => {
        const row = document.createElement("div");
        row.className = "perfume-advisor__history-item";
        if (chat.id === this.chatId) row.classList.add("is-active");

        const openBtn = document.createElement("button");
        openBtn.type = "button";
        openBtn.className = "perfume-advisor__history-open";
        const titleEl = document.createElement("span");
        titleEl.className = "perfume-advisor__history-item-title";
        titleEl.textContent = cleanText(chat.title || "Chat");
        const dateEl = document.createElement("span");
        dateEl.className = "perfume-advisor__history-item-date";
        dateEl.textContent = this.formatHistoryDate(chat.updatedAt);
        openBtn.appendChild(titleEl);
        openBtn.appendChild(dateEl);
        openBtn.addEventListener("click", () => {
          this.saveChat();
          this.loadChatById(chat.id);
          this.hideHistory();
          this.input?.focus({ preventScroll: true });
        });

        const delBtn = document.createElement("button");
        delBtn.type = "button";
        delBtn.className = "perfume-advisor__history-delete";
        delBtn.setAttribute("aria-label", "Delete chat");
        delBtn.textContent = "×";
        delBtn.addEventListener("click", (event) => {
          event.stopPropagation();
          this.deleteChatById(chat.id);
        });

        row.appendChild(openBtn);
        row.appendChild(delBtn);
        this.historyList.appendChild(row);
      });
    }

    snapshotProducts(products = []) {
      return products.map((p) => ({
        id: p.id,
        handle: p.handle,
        title: p.title,
        vendor: p.vendor || "",
        url: p.url,
        image: p.image || "",
        price: p.price,
        variantId: p.variantId,
        available: p.available !== false,
        description: p.description || "",
      }));
    }

    async ensureCatalog() {
      if (!this.catalogReady) this.catalogReady = this.loadCatalog();
      await this.catalogReady;
      return this.catalog;
    }

    async loadCatalog() {
      const seen = new Map();
      await Promise.all(
        CATALOG_COLLECTIONS.map(async (handle) => {
          try {
            for (let page = 1; page <= 3; page += 1) {
              const res = await fetch(`/collections/${handle}/products.json?limit=50&page=${page}`);
              if (!res.ok) break;
              const data = await res.json();
              const products = data.products || [];
              if (!products.length) break;
              products.forEach((product) => {
                const item = this.normalizeProduct(product, handle);
                const existing = seen.get(item.id);
                if (existing) {
                  existing.collections.add(handle);
                  return;
                }
                seen.set(item.id, item);
              });
              if (products.length < 50) break;
            }
          } catch {
            /* ignore */
          }
        })
      );
      this.catalog = [...seen.values()];
      return this.catalog;
    }

    normalizeProduct(product, collectionHandle) {
      const variant = (product.variants || []).find((v) => v.available) || product.variants?.[0] || {};
      const price = Number(variant.price || product.price_min || product.price || 0);
      const description = stripHtml(product.body_html || product.description || product.body || "");
      const tags = Array.isArray(product.tags)
        ? product.tags
        : String(product.tags || "")
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean);
      const rawImage = product.featured_image || product.images?.[0] || product.image || "";
      const image = typeof rawImage === "string" ? rawImage : rawImage.src || rawImage.url || "";
      const text = `${product.title} ${product.vendor || ""} ${product.product_type || product.type || ""} ${tags.join(" ")} ${description}`.toLowerCase();
      return {
        id: product.id,
        handle: product.handle,
        title: product.title,
        vendor: product.vendor || "",
        tags,
        description,
        text,
        url: `/products/${product.handle}`,
        image,
        price,
        variantId: variant.id || null,
        available: product.available !== false && variant.available !== false,
        collections: new Set(collectionHandle ? [collectionHandle] : ["search"]),
      };
    }

    mergeIntoCatalog(items = []) {
      if (!items.length) return;
      const map = new Map(this.catalog.map((p) => [p.id, p]));
      items.forEach((item) => {
        const existing = map.get(item.id);
        if (existing) {
          item.collections.forEach((h) => existing.collections.add(h));
          if (!existing.variantId && item.variantId) existing.variantId = item.variantId;
          if (!existing.image && item.image) existing.image = item.image;
          return;
        }
        map.set(item.id, item);
      });
      this.catalog = [...map.values()];
    }

    async enrichProduct(handle) {
      if (!handle) return null;
      try {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) return null;
        const product = await res.json();
        if (!product?.handle) return null;
        const toMoney = (value) => (value == null || value === "" ? value : Number(value) / 100);
        product.price = toMoney(product.price);
        product.price_min = toMoney(product.price_min);
        product.variants = (product.variants || []).map((variant) => ({
          ...variant,
          price: toMoney(variant.price),
          compare_at_price: toMoney(variant.compare_at_price),
        }));
        const item = this.normalizeProduct(product, "search");
        this.mergeIntoCatalog([item]);
        return item;
      } catch {
        return null;
      }
    }

    async searchStoreProducts(query) {
      const q = String(query || "").trim();
      if (q.length < 2) return [];
      try {
        const url =
          `/search/suggest.json?q=${encodeURIComponent(q)}` +
          `&resources[type]=product&resources[limit]=8`;
        const res = await fetch(url);
        if (!res.ok) return [];
        const data = await res.json();
        const found = data?.resources?.results?.products || [];
        const items = found.map((p) => this.normalizeProduct(p, "search"));
        this.mergeIntoCatalog(items);

        // Suggest API often omits variant ids; enrich top hits for Add to cart.
        await Promise.all(items.slice(0, 4).map((p) => this.enrichProduct(p.handle)));

        const byHandle = new Map(this.catalog.map((p) => [p.handle, p]));
        return items.map((p) => byHandle.get(p.handle) || p).filter(Boolean);
      } catch {
        return [];
      }
    }

    findNameMatches(query) {
      const raw = String(query || "").toLowerCase();
      const stop = new Set([
        "do", "you", "have", "any", "the", "a", "an", "is", "are", "for", "me",
        "please", "want", "need", "looking", "perfume", "perfumes", "fragrance",
        "scent", "lighter", "lighters", "gift", "gifts", "under", "with", "and",
        "or", "in", "stock", "available", "about", "tell", "show", "get",
        "badde", "baddi", "bade", "shu", "la", "fi", "3ande", "3andkon", "3andak",
        "details", "detail", "3atini", "3atine", "a3tini", "hayda", "hayde", "3an", "about", "this", "product",
        "walla3a", "3otr", "3otor", "3itr", "hdiyye", "benet", "bint", "shabb", "zalame",
        "rkhis", "ghali", "kteer", "aw", "ya", "bas", "min", "ana", "ente",
        "hello", "helo", "hi", "hey", "hola", "kifak", "kifik", "keefak", "keifik",
        "marhaba", "ahla", "salam", "friend", "bro", "habibi",
      ]);
      const words = raw
        .split(/[^a-z0-9\u0600-\u06FF]+/i)
        .filter((w) => w.length > 2 && !stop.has(w));
      if (!words.length && !raw.trim()) return [];

      return this.catalog
        .map((product) => {
          const title = product.title.toLowerCase();
          const handle = product.handle.toLowerCase();
          let score = 0;
          if (title === raw.trim() || handle === raw.trim().replace(/\s+/g, "-")) score += 120;
          words.forEach((word) => {
            if (title === word) score += 80;
            if (title.includes(word)) score += 40;
            if (handle.includes(word)) score += 35;
            if (product.text.includes(word)) score += 8;
          });
          // Phrase match for multi-word names
          const phrase = words.join(" ");
          if (phrase.length > 3 && title.includes(phrase)) score += 50;
          return { product, score };
        })
        .filter((row) => row.score >= 35)
        .sort((a, b) => b.score - a.score)
        .map((row) => row.product);
    }

    welcomeText() {
      return "Hi, I’m your Yassine advisor. Looking for a perfume, a lighter, or a gift? Tell me what you need and I’ll guide you to the best match from our shop.";
    }

    isLegacyWelcome(text = "") {
      const t = String(text).toLowerCase();
      return (
        t.includes("ana yassine") ||
        t.includes("badde 3otr") ||
        t.includes("walla3a") ||
        t.includes("ehke ma3e") ||
        t.includes("kif ma baddak")
      );
    }

    start() {
      const bubble = this.addBot(this.welcomeText(), null, null, { showStarters: true });
      this.renderStarters(bubble);
      this.input?.focus({ preventScroll: true });
    }

    renderStarters(bubble) {
      const wrap = document.createElement("div");
      wrap.className = "perfume-advisor__suggestions";
      STARTERS.forEach((label) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "perfume-advisor__suggestion";
        button.textContent = label;
        button.addEventListener("click", () => {
          wrap.querySelectorAll("button").forEach((node) => {
            node.disabled = true;
          });
          this.send(label);
        });
        wrap.appendChild(button);
      });
      bubble.appendChild(wrap);
    }

    async send(text) {
      if (!text || this.busy) return;
      this.addUser(text);
      this.busy = true;
      this.setComposerDisabled(true);
      const typing = this.addTyping();

      try {
        // Load catalog in background; don't block the first ChatGPT reply forever.
        const catalogWait = this.ensureCatalog().catch(() => {});
        await Promise.race([
          catalogWait,
          new Promise((resolve) => setTimeout(resolve, 1800)),
        ]);

        if (this.useAi && this.aiProxyUrl) {
          await this.chatWithAi(text);
        } else {
          this.addBot("ChatGPT isn’t connected yet. Enable Use ChatGPT in the theme editor.");
        }
      } catch (error) {
        console.error(error);
        this.addBot("Something went wrong on my side. Mind trying that again?");
      } finally {
        typing.remove();
        this.busy = false;
        this.setComposerDisabled(false);
        this.input?.focus({ preventScroll: true });
      }
    }

    setComposerDisabled(disabled) {
      if (this.input) this.input.disabled = disabled;
      const send = this.form?.querySelector(".perfume-advisor__send");
      if (send) send.disabled = disabled;
    }

    getStoreContext() {
      return {
        name: "Yassine Group Online",
        location: "Lebanon",
        currency: window.Shopify?.currency?.active || "USD",
        current_product: this.currentProductHandle() || null,
        free_delivery_above: 70,
        whatsapp: "+961 78 783 552",
        phone: "+961 78 783 552",
        email: "yassinegroup-lb@hotmail.com",
        address: "Amliyeh Street, Haret Hreik, Beirut, Lebanon",
        contact_page: "https://yassinegrouplb.com/pages/contact",
        instagram: "https://www.instagram.com/yassinegrouplb",
        tiktok: "https://www.tiktok.com/@yassinegrouponline",
        cash_on_delivery: true,
        categories: ["perfume", "lighter", "gift"],
        brands: Object.keys(BRANDS),
        languages: ["English", "Arabic", "Lebanese dialect", "Arabizi", "French"],
        note: "Salesman for perfume, lighter, gifts, and store contact. WhatsApp and phone questions are in scope: answer +961 78 783 552. Match shopper language. Off-topic other than contact → refuse in English. Keep perfume/lighter/gift in English. 3otor means perfume.",
      };
    }

    isContactAsk(text = "") {
      const t = String(text).toLowerCase();
      return /\b(whats?\s*app|phone|number|raqam|ra2em|ra2am|contact|email|e-mail|mail|address|location|instagram|facebook|tiktok|call|kallim|wein|where are you)\b|واتس|واتساب|رقم|تواصل|ايميل|عنوان/.test(
        t
      );
    }

    contactAnswer(text = "") {
      const lang = this.replyLanguage(text);
      const t = String(text).toLowerCase();
      const phone = "+961 78 783 552";
      const email = "yassinegroup-lb@hotmail.com";
      const address = "Amliyeh Street, Haret Hreik, Beirut";
      const page = "https://yassinegrouplb.com/pages/contact";
      const wantsPhone = /whats?\s*app|phone|number|raqam|ra2em|ra2am|call|kallim|واتس|واتساب|رقم/.test(t);
      const wantsEmail = /email|e-mail|mail|ايميل/.test(t);
      const wantsAddress = /address|location|where are you|wein|عنوان/.test(t);
      const wantsSocial = /instagram|facebook|tiktok/.test(t);
      const general = !wantsPhone && !wantsEmail && !wantsAddress && !wantsSocial;

      if (lang === "lebanese") {
        if (wantsPhone && !general) return `El WhatsApp w el phone: ${phone}.`;
        if (wantsEmail) return `El email: ${email}.`;
        if (wantsAddress) return `El address: ${address}.`;
        if (wantsSocial) return "Instagram yassinegrouplb, TikTok yassinegrouponline.";
        return `Tawasal ma3na 3al WhatsApp ${phone}. Email ${email}. Address ${address}. ${page}`;
      }
      if (lang === "arabic") {
        if (wantsPhone && !general) return `واتساب والهاتف: ${phone}.`;
        if (wantsEmail) return `الإيميل: ${email}.`;
        if (wantsAddress) return `العنوان: ${address}.`;
        if (wantsSocial) return "انستغرام yassinegrouplb، تيك توك yassinegrouponline.";
        return `تواصل معنا على واتساب ${phone}. الإيميل ${email}. العنوان ${address}. ${page}`;
      }
      if (lang === "french") {
        if (wantsPhone && !general) return `WhatsApp et téléphone : ${phone}.`;
        if (wantsEmail) return `Email : ${email}.`;
        if (wantsAddress) return `Adresse : ${address}.`;
        if (wantsSocial) return "Instagram yassinegrouplb, TikTok yassinegrouponline.";
        return `Contactez nous sur WhatsApp ${phone}. Email ${email}. Adresse ${address}. ${page}`;
      }
      if (wantsPhone && !general) return `WhatsApp and phone: ${phone}.`;
      if (wantsEmail) return `Email: ${email}.`;
      if (wantsAddress) return `Address: ${address}.`;
      if (wantsSocial) return "Instagram is yassinegrouplb. TikTok is yassinegrouponline.";
      return `WhatsApp and phone: ${phone}. Email: ${email}. Address: ${address}. Contact page: ${page}. We deliver across Lebanon, with free delivery above $70, and cash on delivery.`;
    }

    isOffTopic(text = "") {
      const t = String(text).toLowerCase();
      if (this.isSmallTalk(t) || this.isContactAsk(t)) return false;
      if (
        /\b(perfume|perfumes|fragrance|scent|lighter|lighters|zippo|gift|gifts|badde|baddi|bade|3otr|3otor|oud|yara|nabatieh|lattafa|rasasi|budget|price|kam|benet|shabb|brand|cart|order|delivery|hdiyye)\b|عطر|ولاعة|هدية|\$\d|\d+\s*\$/.test(
          t
        )
      ) {
        return false;
      }
      return /\b(weather|politics|election|trump|war|israel|palestine|code|javascript|python|homework|recipe|cook|medical|doctor|crypto|bitcoin|stock|religion|hack|password|nude|sex|chatgpt|openai|who is|what is the capital|math|solve)\b/i.test(
        t
      );
    }

    isSmallTalk(text = "") {
      const t = String(text).toLowerCase().trim().replace(/[!?.]+$/g, "");
      if (!t) return true;
      if (
        /^(hi|hello|helo|hey|yo|hola|salut|marhaba|ahla|hiya|salam|helloo+)\b/.test(t) &&
        !/\b(perfume|gift|lighter|badde|baddi|3otr|nabatieh|yara)\b/.test(t)
      ) {
        return true;
      }
      if (
        /^(kifak|kifik|keefak|keifik|shu\s*akhbar|sho\s*akhbar|good\s*(morning|evening|night)|sabah el kher|masa el kher)\b/.test(t)
      ) {
        return true;
      }
      // short social openers like "hello kifak"
      const words = t.split(/\s+/).filter(Boolean);
      if (
        words.length <= 4 &&
        words.every((w) =>
          /^(hi|hello|helo|hey|yo|kifak|kifik|keefak|keifik|marhaba|ahla|salam|friend|bro|habibi)$/.test(w)
        )
      ) {
        return true;
      }
      return false;
    }

    isSpecificProductAsk(text = "") {
      if (this.isSmallTalk(text)) return false;
      const t = String(text).toLowerCase();
      const stop = new Set([
        "hello", "helo", "hi", "hey", "hola", "kifak", "kifik", "keefak", "keifik",
        "marhaba", "ahla", "salam", "friend", "bro", "habibi", "please", "thanks",
        "thank", "you", "the", "and", "for", "gift", "perfume", "lighter",
      ]);

      if (
        /\bgift\b|\bhdiyye\b|\bbadde\b|\bbaddi\b|\bbade\b|\bbudget\b|\$\s*\d|\d+\s*\$/.test(t) &&
        !/\b(nabatieh|yara|khamrah|hawas|asdaaf|angham|qaed|lattafa|rasasi|zippo)\b/i.test(t)
      ) {
        return false;
      }

      const explicit =
        /\b(do you have|have you|3andkon|3andak|fi 3and|available|in stock)\b/i.test(t);
      if (explicit) return true;

      // Strong name match only (ignore greeting words like "hello")
      return this.findNameMatches(text).some((p) => {
        const titleWords = p.title
          .toLowerCase()
          .split(/[^a-z0-9\u0600-\u06FF]+/i)
          .filter((w) => w.length > 3 && !stop.has(w));
        return titleWords.some((w) => t.includes(w));
      });
    }

    hasBudget(filters = {}) {
      return filters.maxPrice != null || filters.minPrice != null || filters.budgetKnown === true;
    }

    hasAudience(filters = {}) {
      return Boolean(filters.audience && filters.audience !== "gift");
    }

    hasEnoughNeedToRecommend(filters = {}) {
      if (!filters.category) return false;
      if (filters.category === "lighter") return true;
      // Perfume: must know Women / Men / Kids / for me before suggesting products.
      return this.hasAudience(filters);
    }

    nextMissingPrompt(filters = {}) {
      const whoKnown = this.hasAudience(filters);
      if (!filters.category) {
        return whoKnown
          ? { reply: "Perfume or a lighter?", chips: ["Perfume", "Lighter"] }
          : {
              reply: "Looking for perfume or a lighter? And is it for women, men, or kids?",
              chips: ["Perfume for her", "Perfume for him", "Kids perfume", "Lighter"],
            };
      }
      if (filters.category === "perfume" && !whoKnown) {
        return {
          reply: "Is this perfume for women, men, or kids?",
          chips: ["Women", "Men", "Kids", "For me"],
        };
      }
      if (!this.hasBudget(filters)) {
        return {
          reply: "What budget works for you?",
          chips: ["Under $40", "$40 to $70", "$70 to $100", "No limit"],
        };
      }
      if (filters.category === "perfume" && !filters.vibe && !filters.note) {
        return {
          reply: "What scent are you after? Fresh, sweet, floral, oud, or a note like vanilla?",
          chips: ["Fresh", "Sweet", "Floral", "Oud", "Vanilla"],
        };
      }
      return null;
    }

    extractRequestedNote(text = "") {
      const t = String(text).toLowerCase();
      if (!t.trim()) return null;
      // Prefer longer / more specific keys first.
      const keys = Object.keys(NOTE_ALIASES).sort((a, b) => b.length - a.length);
      for (const key of keys) {
        const aliases = NOTE_ALIASES[key];
        if (aliases.some((a) => new RegExp(`\\b${a}\\b`, "i").test(t))) return key;
      }
      return null;
    }

    productHasNote(product, note) {
      if (!product || !note) return false;
      const aliases = NOTE_ALIASES[note] || [note];
      const blob = `${product.title || ""} ${product.description || ""} ${product.text || ""}`.toLowerCase();
      return aliases.some((a) => blob.includes(a));
    }

    pyramidHasNote(description = "", note, bands = ["top", "heart", "middle", "mid"]) {
      const aliases = NOTE_ALIASES[note] || [note];
      const desc = String(description || "");
      if (!desc) return false;
      // Stop each band at the next Top/Heart/Middle/Base label (inline pyramids have no periods).
      const bandRe =
        /\b(top|heart|middle|mid|base)\s*(?:notes?)?\s*[:\-–]\s*([\s\S]*?)(?=\b(?:top|heart|middle|mid|base)\s*(?:notes?)?\s*[:\-–]|$)/gi;
      let match;
      while ((match = bandRe.exec(desc))) {
        const band = match[1].toLowerCase();
        if (!bands.includes(band)) continue;
        const block = match[2].toLowerCase().slice(0, 180);
        if (aliases.some((a) => block.includes(a))) return true;
      }
      return false;
    }

    noteMatchStrength(product, note) {
      if (!this.productHasNote(product, note)) return 0;
      const aliases = NOTE_ALIASES[note] || [note];
      const title = String(product.title || "").toLowerCase();
      const desc = String(product.description || product.text || "").toLowerCase();
      if (aliases.some((a) => title.includes(a))) return 3;
      // Strong: marketed as a [note] fragrance, or note in top/heart (not only a quiet base).
      const marketed = aliases.some((a) => {
        const esc = a.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        return new RegExp(`${esc}\\s+fragrance|oriental\\s+${esc}|fragrance[^.]{0,48}${esc}`, "i").test(
          desc
        );
      });
      if (marketed || this.pyramidHasNote(desc, note, ["top", "heart", "middle", "mid"])) {
        return 2;
      }
      // Weak: only a base note or a loose mention — not a true "[note] perfume".
      return 1;
    }

    filterProductsForNote(products = [], note) {
      if (!note || !Array.isArray(products)) return products || [];
      const matched = products.filter((p) => this.productHasNote(p, note));
      const strong = matched.filter((p) => this.noteMatchStrength(p, note) >= 2);
      // Prefer products where the note is listed in fragrance notes / title.
      // Only fall back to weak single-word mentions if nothing stronger exists.
      const pool = strong.length ? strong : matched;
      return pool.sort(
        (a, b) => this.noteMatchStrength(b, note) - this.noteMatchStrength(a, note)
      );
    }

    extractNoteSummary(product) {
      const desc = String(product?.description || "");
      if (!desc) return "";
      const m =
        desc.match(/fragrance notes?\s*[:\-–]?\s*([\s\S]{10,280}?)(?:product information|this is|launched|$)/i) ||
        desc.match(/(?:top|heart|middle|base)\s*notes?\s*[:\-–]?\s*([\s\S]{10,220})/i);
      const snippet = (m ? m[0] : desc).replace(/\s+/g, " ").trim().slice(0, 220);
      return snippet;
    }

    inferPrefsFromText(text = "") {
      const t = String(text).toLowerCase().trim();
      const out = {};
      // Kids / boys / girls / Disney first — never treat as adult women or men.
      if (
        /\bkids?\b|\bchildren\b|\bboys?\b|\bgirls?\b|\bfor\s+(a\s+)?(kid|child|boy|girl)s?\b|\bdisney\b|\btubbees\b|للولاد|للاولاد|للأولاد|للطفل|للاطفال|للأطفال/.test(t)
      ) {
        out.audience = "kids";
      } else if (
        /\bla\s*benet\b|\bbint\b|\bfor her\b|\bwomen\b|\bwoman\b|\bfemale\b|\blady\b|\bladies\b|للبنت|للبنات|للنساء|للمرأ/.test(t)
      ) {
        out.audience = "women";
      } else if (
        /\bla\s*shabb\b|\bzalame\b|\bfor him\b|\bmen\b|\bman\b|\bmale\b|\bguy\b|للشب|للشباب|للرجل/.test(t)
      ) {
        out.audience = "men";
      } else if (/\bfor me\b|\blal\s*hal\b|\bunisex\b/.test(t)) out.audience = "unisex";
      else if (/\bgift\b|\bhdiyye\b|هدية/.test(t)) out.audience = "gift";

      if (/\bperfume\b|\b3otr\b|\b3otor\b|\b3itr\b|عطر|fragrance|scent/.test(t)) out.category = "perfume";
      if (/\blighter\b|\bwalla3a\b|\bzippo\b|ولاعة/.test(t)) out.category = "lighter";

      if (/under\s*\$?\s*40|<\s*\$?\s*40/.test(t)) {
        out.minPrice = 0;
        out.maxPrice = 40;
        out.budgetKnown = true;
      } else if (/\$?\s*40\s*(to|-|–)\s*\$?\s*70|40\s*to\s*70/.test(t)) {
        out.minPrice = 40;
        out.maxPrice = 70;
        out.budgetKnown = true;
      } else if (/\$?\s*70\s*(to|-|–)\s*\$?\s*100|70\s*to\s*100/.test(t)) {
        out.minPrice = 70;
        out.maxPrice = 100;
        out.budgetKnown = true;
      } else if (/no limit|any budget|ma fi budget/.test(t)) {
        out.minPrice = 0;
        out.maxPrice = 99999;
        out.budgetKnown = true;
      } else {
        const budget = t.match(/(?:under\s*|b\s*|\$\s*)(\d{2,4})/) || t.match(/(\d{2,4})\s*\$/);
        if (budget) {
          out.maxPrice = Number(budget[1]);
          out.budgetKnown = true;
        }
      }

      const note = this.extractRequestedNote(t);
      if (note) {
        out.note = note;
        if (["vanilla", "caramel", "chocolate", "honey", "pistachio", "coconut", "coffee"].includes(note)) {
          out.vibe = "sweet";
        } else if (["rose", "jasmine", "lavender"].includes(note)) {
          out.vibe = "floral";
        } else if (["oud", "amber", "saffron", "musk"].includes(note)) {
          out.vibe = "oriental";
        } else if (["sandalwood", "leather", "tobacco"].includes(note)) {
          out.vibe = "woody";
        } else if (note === "citrus") {
          out.vibe = "fresh";
        }
      } else if (/\bfresh\b|\bcitrus\b|\bclean\b/.test(t)) out.vibe = "fresh";
      else if (/\bsweet\b|\bgourmand\b/.test(t)) out.vibe = "sweet";
      else if (/\bfloral\b/.test(t)) out.vibe = "floral";
      else if (/\boriental\b/.test(t)) out.vibe = "oriental";
      else if (/\bwoody\b|\bspicy\b/.test(t)) out.vibe = "woody";

      // Exact chip taps
      if (t === "fresh") out.vibe = "fresh";
      if (t === "sweet") out.vibe = "sweet";
      if (t === "floral") out.vibe = "floral";
      if (t === "oud") {
        out.vibe = "oriental";
        out.note = "oud";
      }
      if (t === "vanilla") {
        out.vibe = "sweet";
        out.note = "vanilla";
      }
      if (t === "women" || t === "for her" || t === "perfume for her") {
        out.audience = "women";
        out.category = out.category || "perfume";
      }
      if (t === "men" || t === "for him" || t === "perfume for him") {
        out.audience = "men";
        out.category = out.category || "perfume";
      }
      if (t === "kids" || t === "kids perfume") {
        out.audience = "kids";
        out.category = "perfume";
      }
      if (t === "for me") out.audience = "unisex";
      if (t === "perfume") out.category = "perfume";
      if (t === "lighter") out.category = "lighter";

      return out;
    }

    chipsForMessage(reply = "", aiQuick = []) {
      const r = String(reply).toLowerCase();
      const asksBudget = /\bb\s*kam\b|\bbkam\b|\bbudget\b|how much|price|kam\s*\?|b\s*\$/.test(r);
      const asksType = /type of perfume|shu type|fresh|sweet|floral|oud|bteheb/.test(r) && !asksBudget;
      const asksCategory = /perfume aw lighter|perfume or lighter|perfume.*lighter|lighter.*perfume/.test(r);
      const asksWho =
        /la meen|who (is it )?for|for women|for men|for kids|women, men, or kids|is this perfume for/.test(r) &&
        !asksBudget &&
        !asksType;

      const toChips = (labels) =>
        labels.map((label, i) => ({ id: `c-${i}`, label }));

      if (asksBudget) {
        return toChips(["Under $40", "$40 to $70", "$70 to $100", "No limit"]);
      }
      if (asksType) {
        return toChips(["Fresh", "Sweet", "Floral", "Oud", "Vanilla"]);
      }
      if (asksCategory && asksWho) {
        return toChips(["Perfume for her", "Perfume for him", "Kids perfume", "Lighter"]);
      }
      if (asksCategory) {
        return toChips(["Perfume", "Lighter"]);
      }
      if (asksWho) {
        return toChips(["Women", "Men", "Kids", "For me"]);
      }

      const cleaned = (aiQuick || [])
        .filter((c) => c?.label)
        .map((c) => c.label.trim())
        .filter(Boolean)
        .slice(0, 4);

      if (!cleaned.length) return null;
      return cleaned.map((label, i) => ({ id: `q-${i}`, label }));
    }

    buildRecommendIntro(products = []) {
      if (!products.length) return "Here are some great matches from our shop:";
      const bits = products.slice(0, 2).map((p) => `${cleanText(p.title)} ${this.formatMoney(p.price)}`);
      if (bits.length === 1) return `Perfect, try ${bits[0]}. Shu ra2yak?`;
      return `Perfect, masalan ${bits[0]}, aw ${bits[1]}. Shu ra2yak?`;
    }

    isLighterProduct(product) {
      if (!product) return false;
      if ([...product.collections].some((c) => LIGHTER_HANDLES.has(c))) return true;
      const blob = `${product.title} ${product.vendor} ${product.text}`.toLowerCase();
      return /\blighter\b|\bzippo\b/.test(blob);
    }

    buildCandidates(query, priority = []) {
      const prefs = { query, ...this.prefs };
      let scored = this.scoreCatalog(prefs);
      scored = this.filterProductsForAudience(scored, prefs.audience);
      if (prefs.note) scored = this.filterProductsForNote(scored, prefs.note);

      const seen = new Set();
      let list = [];
      const priorityFiltered = this.filterProductsForNote(
        this.filterProductsForAudience(priority, prefs.audience),
        prefs.note
      );
      [...priorityFiltered, ...scored].forEach((p) => {
        if (!p || seen.has(p.id)) return;
        seen.add(p.id);
        list.push(p);
      });
      if (list.length < 12 && prefs.audience) {
        const fillers = this.filterProductsForNote(
          this.filterProductsForAudience(
            this.catalog.filter((p) => p.available && p.collections.has("best-seller")),
            prefs.audience
          ),
          prefs.note
        ).slice(0, 16);
        fillers.forEach((p) => {
          if (!seen.has(p.id)) {
            seen.add(p.id);
            list.push(p);
          }
        });
      }
      list = list.slice(0, 40);
      return list.map((p) => ({
        handle: p.handle,
        title: p.title,
        vendor: p.vendor,
        price: p.price,
        category: this.isLighterProduct(p) ? "lighter" : "perfume",
        audience: this.isKidsProduct(p)
          ? "kids"
          : p.collections?.has?.("women")
            ? "women"
            : p.collections?.has?.("men")
              ? "men"
              : p.collections?.has?.("unisex-perfumes")
                ? "unisex"
                : "unknown",
        notes: this.extractNoteSummary(p),
        has_requested_note: prefs.note ? this.productHasNote(p, prefs.note) : null,
        note_strength: prefs.note ? this.noteMatchStrength(p, prefs.note) : null,
      }));
    }

    productsByHandles(handles = []) {
      const map = new Map(this.catalog.map((p) => [p.handle, p]));
      return handles.map((h) => map.get(h)).filter(Boolean).slice(0, 4);
    }

    normalizeFilters(filters = {}) {
      const out = { ...this.prefs };
      if (filters.audience && AUDIENCE[filters.audience]) out.audience = filters.audience;
      if (filters.vibe && VIBES[filters.vibe]) out.vibe = filters.vibe;
      if (filters.category && CATEGORIES[filters.category]) out.category = filters.category;
      if (filters.note) {
        const note = String(filters.note).toLowerCase().trim();
        if (NOTE_ALIASES[note]) out.note = note;
      }
      if (filters.brand) {
        const key = String(filters.brand).toLowerCase().trim();
        if (BRANDS[key]) out.brand = key;
      }
      if (filters.minPrice != null && !Number.isNaN(Number(filters.minPrice))) {
        out.minPrice = Number(filters.minPrice);
        out.budgetKnown = true;
      }
      if (filters.maxPrice != null && !Number.isNaN(Number(filters.maxPrice))) {
        out.maxPrice = Number(filters.maxPrice);
        out.budgetKnown = true;
      }
      if (filters.budgetKnown) out.budgetKnown = true;
      if (filters.intensity) out.intensity = filters.intensity;
      if (filters.occasion) out.occasion = filters.occasion;
      if (filters.season) out.season = filters.season;
      return out;
    }

    currentProductHandle() {
      const match = window.location.pathname.match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]) : "";
    }

    replyLanguage(text = "") {
      const t = String(text);
      if (/[\u0600-\u06FF]/.test(t)) return "arabic";
      if (
        /\b(kifak|kiifik|badde|baddi|shu|shou|3atini|3atine|a3tini|hayda|hayde|3an|marhaba|yalla|wallah|kteer|3ande|3andi)\b/i.test(
          t
        )
      ) {
        return "lebanese";
      }
      if (/\b(bonjour|salut|je veux|merci)\b/i.test(t)) return "french";
      return "english";
    }

    isDetailAsk(text = "") {
      const t = String(text).toLowerCase();
      const asks =
        /\b(details?|more info|more information|tell me about|tell me more|describe|tafas\w*|3atini|3atine|a3tini|a3tine)\b/.test(
          t
        ) || /تفاصيل|عطيني|أعطيني/.test(t);
      if (!asks) return false;
      return (
        /\b(perfume|perfumes|lighter|lighters|zippo|gift|gifts|product|products|hayda|hayde|hada|this|3otr|3otor)\b/.test(
          t
        ) ||
        /عطر|ولاعة|هدية|منتج/.test(t) ||
        Boolean(this.currentProductHandle()) ||
        Boolean(this.lastProducts?.length)
      );
    }

    productKind(product) {
      const blob = `${product?.title || ""} ${product?.vendor || ""} ${product?.description || ""} ${product?.text || ""}`.toLowerCase();
      if (/\blighter\b|\bzippo\b/.test(blob)) return "lighter";
      if (product?.collections && this.isLighterProduct(product)) return "lighter";
      if (/\bgift set\b|\bbundle\b/.test(blob)) return "gift";
      return "perfume";
    }

    detailBits(product) {
      const raw = String(product?.description || "").replace(/\s+/g, " ").trim();
      const grab = (label, stop) => {
        const match = raw.match(
          new RegExp(`${label}\\s*:\\s*(.+?)(?=\\s+(?:${stop})\\s*:|$)`, "i")
        );
        return match ? match[1].replace(/\s+/g, " ").trim() : "";
      };
      let about = raw;
      if (/product information\s*:/i.test(raw)) {
        about = raw.split(/product information\s*:/i)[1] || raw;
      }
      about = about.replace(/\s+/g, " ").trim();
      if (about.length > 220) about = `${about.slice(0, 217).trim()}…`;
      return {
        about,
        top: grab("Top", "Heart|Base|Product Information"),
        heart: grab("Heart", "Base|Product Information"),
        base: grab("Base", "Product Information"),
      };
    }

    buildDetailPitch(product, text) {
      const lang = this.replyLanguage(text);
      const name = cleanText(product.title);
      const price = this.formatMoney(product.price);
      const kind = this.productKind(product);
      const stock = product.available !== false;
      if (lang === "lebanese") {
        return stock
          ? `Hayda ${name}, ${kind} helw w mawjoud 3anna b ${price}. Shuf el details ta7t, w iza 3ajabak add to cart.`
          : `Hayda ${name} halla2 sold out. Shuf el details ta7t, w iza baddak bfarjik shi tene.`;
      }
      if (lang === "arabic") {
        return stock
          ? `هذا ${name}، ${kind} متوفر بسعر ${price}. شوف التفاصيل تحت، وإذا عجبك اضغط Add to cart.`
          : `هذا ${name} حالياً sold out. شوف التفاصيل تحت وإذا بدك بساعدك تختار غيره.`;
      }
      if (lang === "french") {
        return stock
          ? `${name} est un beau choix. Ce ${kind} est disponible à ${price}. Voici les détails, vous pouvez l'ajouter au panier.`
          : `${name} est en rupture. Voici les détails, je peux vous proposer autre chose.`;
      }
      return stock
        ? `${name} is a great pick. This ${kind} is in stock at ${price}. Here are the details. Add it to your cart if you want it.`
        : `${name} is sold out right now. Here are the details, and I can help you pick another one.`;
    }

    async resolveDetailProduct(text) {
      const t = String(text).toLowerCase();
      const named = this.findNameMatches(text);
      const namedHit = named.find((product) => {
        const words = product.title
          .toLowerCase()
          .split(/[^a-z0-9\u0600-\u06FF]+/i)
          .filter((word) => word.length > 3);
        return words.some((word) => t.includes(word));
      });
      if (namedHit) return (await this.enrichProduct(namedHit.handle)) || namedHit;

      const wantsThis =
        /\b(this|hayda|hayde|hada|hal)\b/.test(t) ||
        /\b(the|l|el)\s+(perfume|lighter|product|gift)\b/.test(t);
      const pageHandle = this.currentProductHandle();
      if (pageHandle && wantsThis) {
        const pageProduct = await this.enrichProduct(pageHandle);
        if (pageProduct) return pageProduct;
      }
      if (pageHandle && !named.length) {
        const pageProduct = await this.enrichProduct(pageHandle);
        if (pageProduct) return pageProduct;
      }
      if (wantsThis && this.lastProducts?.[0]) {
        return (await this.enrichProduct(this.lastProducts[0].handle)) || this.lastProducts[0];
      }

      const wantLighter = /\blighter|zippo|walla/.test(t);
      const available = (this.catalog || []).filter((product) => product.available !== false);
      const bestsellers = available.filter((product) => product.collections?.has?.("best-seller"));
      const pool = (bestsellers.length ? bestsellers : available).filter((product) =>
        wantLighter ? this.isLighterProduct(product) : !this.isLighterProduct(product)
      );
      const pick = pool[0] || available[0];
      if (!pick) return null;
      return (await this.enrichProduct(pick.handle)) || pick;
    }

    async chatWithAi(text) {
      this.chatMessages.push({ role: "user", content: text });
      if (this.isContactAsk(text)) {
        const answer = this.contactAnswer(text);
        this.chatMessages.push({ role: "assistant", content: answer });
        this.addBot(
          answer,
          [
            { id: "r1", label: "Perfume" },
            { id: "r2", label: "Lighter" },
            { id: "r3", label: "Gift" },
          ],
          null
        );
        return;
      }

      const smallTalk = this.isSmallTalk(text);
      const offTopic = !smallTalk && this.isOffTopic(text);
      if (!smallTalk && !offTopic) {
        this.prefs = this.normalizeFilters({ ...this.prefs, ...this.inferPrefsFromText(text) });
      }

      if (offTopic) {
        const refuse =
          "I'm not authorised to answer this question. How can I help you with perfume, a lighter, or a gift?";
        this.chatMessages.push({ role: "assistant", content: refuse });
        this.addBot(
          refuse,
          [
            { id: "r1", label: "Perfume" },
            { id: "r2", label: "Lighter" },
            { id: "r3", label: "Gift" },
          ],
          null
        );
        return;
      }

      await Promise.race([
        this.ensureCatalog().catch(() => {}),
        new Promise((resolve) => setTimeout(resolve, 8000)),
      ]);

      if (!smallTalk && this.isDetailAsk(text)) {
        const detailProduct = await this.resolveDetailProduct(text);
        if (detailProduct) {
          const reply = this.buildDetailPitch(detailProduct, text);
          this.chatMessages.push({ role: "assistant", content: reply });
          this.lastProducts = [detailProduct];
          this.addBot(reply, null, [detailProduct], { detail: true });
          return;
        }
      }

      const specificAsk = !smallTalk && this.isSpecificProductAsk(text);
      let uniqueMatches = [];
      if (specificAsk) {
        const searched = await this.searchStoreProducts(text);
        const nameMatches = [...searched, ...this.findNameMatches(text)];
        const seenMatch = new Set();
        nameMatches.forEach((p) => {
          if (!p || seenMatch.has(p.id)) return;
          seenMatch.add(p.id);
          uniqueMatches.push(p);
        });
      }

      const candidates = smallTalk ? [] : this.buildCandidates(text, uniqueMatches);
      const store = {
        ...this.getStoreContext(),
        named_matches: uniqueMatches.slice(0, 6).map((p) => ({
          handle: p.handle,
          title: p.title,
          price: p.price,
        })),
        note: smallTalk
          ? "Greeting/small talk. Friendly chat only. action=chat, no products. Reply in SAME language as user (Lebanese Arabizi if they used kifak/badde/etc)."
          : specificAsk && uniqueMatches.length
            ? "Named product ask. Confirm and recommend matches. Match user language."
            : "ACCURACY LOCK: For perfume, if audience (women/men/kids/unisex) is unknown, action=chat and ask Women, Men, or Kids — do not recommend yet. Never mix kids products with adult perfume. Only recommend handles from candidates. Never invent notes — only use notes shown on each candidate. If shopper asked for a specific note (e.g. vanilla), only recommend candidates where has_requested_note is true / notes text contains that note. Prefer higher note_strength. Match user language.",
        known_prefs: smallTalk ? {} : this.prefs,
      };

      let data;
      try {
        const response = await fetch(this.aiProxyUrl.replace(/\/$/, ""), {
          method: "POST",
          mode: "cors",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            messages: this.chatMessages,
            store,
            candidates: specificAsk ? candidates : smallTalk ? [] : candidates.slice(0, 16),
          }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || payload.error) {
          throw new Error(payload.detail || payload.error || `Proxy ${response.status}`);
        }
        data = payload;
      } catch (error) {
        console.warn("AI error:", error);
        this.chatMessages.pop();
        if (smallTalk) {
          this.addBot(
            "Ahla! Kifak? Ana Yassine advisor, badde sa3dak tla2e perfume, lighter, aw gift.",
            [
              { id: "q1", label: "Looking for a gift" },
              { id: "q2", label: "I need a perfume" },
              { id: "q3", label: "I want a lighter" },
            ],
            null
          );
          return;
        }
        if (specificAsk && uniqueMatches.length) {
          const pages = this.splitProductPages(uniqueMatches);
          this.addBot(
            `Yes, we have ${uniqueMatches[0].title} in stock. Here it is:`,
            null,
            pages.shown,
            { moreProducts: pages.more }
          );
          return;
        }
        this.addBot(
          "Sure. Looking for a perfume, a lighter, or a gift?",
          [
            { id: "q1", label: "Perfume" },
            { id: "q2", label: "Lighter" },
            { id: "q3", label: "A gift" },
          ],
          null
        );
        return;
      }

      if (data.action === "refuse") {
        const refuse =
          "I'm not authorised to answer this question. How can I help you with perfume, a lighter, or a gift?";
        this.chatMessages.push({ role: "assistant", content: refuse });
        this.addBot(
          refuse,
          [
            { id: "r1", label: "Perfume" },
            { id: "r2", label: "Lighter" },
            { id: "r3", label: "Gift" },
          ],
          null
        );
        return;
      }

      if (!smallTalk) {
        this.prefs = this.normalizeFilters({ ...this.prefs, ...data.filters });
      }

      const namedReady = specificAsk && uniqueMatches.length > 0 && data.action === "recommend";
      const prefsNow = this.prefs;
      const missing = smallTalk || namedReady ? null : this.nextMissingPrompt(prefsNow);
      // Trust the salesman AI: recommend when it says so and we have enough signal.
      const readyToSell =
        !smallTalk &&
        (namedReady ||
          (data.action === "recommend" && this.hasEnoughNeedToRecommend(prefsNow)));

      let reply = String(data.message || "").trim();
      let productPool = [];

      if (smallTalk) {
        reply =
          reply ||
          "Ahla! Ana mni7, kifak enta? Badde sa3dak tla2e perfume, lighter, aw gift?";
        productPool = [];
      } else if (readyToSell) {
        productPool = this.productsByHandles(data.product_handles);
        if (!productPool.length && uniqueMatches.length) {
          productPool = [...uniqueMatches];
        }
        productPool = this.filterProductsForAudience(productPool, prefsNow.audience);
        if (prefsNow.note) {
          productPool = this.filterProductsForNote(productPool, prefsNow.note);
        }
        if (productPool.length < PRODUCT_PAGE_SIZE) {
          const terms = Array.isArray(data.search_terms) ? data.search_terms.join(" ") : "";
          const scored = this.scoreCatalog({
            ...this.prefs,
            query: [terms, text, this.prefs.note || "", this.prefs.vibe || ""]
              .filter(Boolean)
              .join(" "),
          }).slice(0, PRODUCT_POOL_SIZE);
          const seen = new Set(productPool.map((p) => p.id));
          scored.forEach((p) => {
            if (!seen.has(p.id)) {
              seen.add(p.id);
              productPool.push(p);
            }
          });
          productPool = this.filterProductsForAudience(
            productPool.slice(0, PRODUCT_POOL_SIZE),
            prefsNow.audience
          );
          if (prefsNow.note) {
            productPool = this.filterProductsForNote(productPool, prefsNow.note);
          }
        }
        // Prefer strong note matches first (e.g. Vanilla in the title over a weak base note).
        if (prefsNow.note) {
          productPool = [...productPool].sort(
            (a, b) =>
              this.noteMatchStrength(b, prefsNow.note) - this.noteMatchStrength(a, prefsNow.note)
          );
        }
        // If AI invented prices, replace with accurate intro from catalog cards.
        if (!reply || /\$\s*\d/.test(reply)) {
          reply = this.buildRecommendIntro(productPool);
        }
        if (!productPool.length && prefsNow.note) {
          reply = `I couldn't find a solid ${prefsNow.note} match for that in stock right now. Want another note, or a different audience?`;
        }
      } else if (data.action === "recommend" && missing) {
        // Too early to sell: ask gender / budget / scent before showing products.
        reply = missing.reply;
      } else if (!reply && missing) {
        reply = missing.reply;
      } else if (!reply) {
        reply = "Sure, how can I help you find a perfume, lighter, or gift?";
      }

      if (namedReady) {
        const denied = /don'?t have|do not have|no (info|information)|not (in|available)|couldn'?t find|cannot find|can't find|unavailable/i.test(String(data.message || ""));
        if (denied || !productPool.length) {
          productPool = uniqueMatches.slice(0, PRODUCT_POOL_SIZE);
          reply = this.buildRecommendIntro(productPool);
        }
      }

      this.chatMessages.push({ role: "assistant", content: reply });

      const pages = this.splitProductPages(productPool);
      const aiQuick = (data.quick_replies || [])
        .filter((label) => typeof label === "string" && label.trim())
        .slice(0, 4)
        .map((label, i) => ({ id: `q-${i}`, label: label.trim() }));

      let chips = null;
      if (!pages.shown.length) {
        if (smallTalk) {
          chips = [
            { id: "s1", label: "Looking for a gift" },
            { id: "s2", label: "I need a perfume" },
            { id: "s3", label: "I want a lighter" },
          ];
        } else {
          // Prefer chips that match the message; fall back to AI or next missing step.
          chips = this.chipsForMessage(reply, aiQuick);
          if (!chips?.length && missing) {
            chips = missing.chips.map((label, i) => ({ id: `c-${i}`, label }));
          }
        }
      }

      this.addBot(
        reply,
        chips,
        pages.shown.length ? pages.shown : null,
        { moreProducts: pages.more }
      );
    }

    isKidsProduct(product) {
      const kids = AUDIENCE.kids.handles;
      return kids.some((h) => product?.collections?.has?.(h));
    }

    filterProductsForAudience(products = [], audience) {
      if (!Array.isArray(products)) return [];
      if (!audience || audience === "gift") {
        // Never mix kids into adult browsing when audience is unset/gift.
        return products.filter((p) => !this.isKidsProduct(p));
      }
      if (audience === "kids") {
        return products.filter((p) => this.isKidsProduct(p));
      }
      if (audience === "women") {
        return products.filter(
          (p) => p?.collections?.has?.("women") && !this.isKidsProduct(p)
        );
      }
      if (audience === "men") {
        return products.filter(
          (p) => p?.collections?.has?.("men") && !this.isKidsProduct(p)
        );
      }
      if (audience === "unisex") {
        return products.filter(
          (p) =>
            !this.isKidsProduct(p) &&
            (p?.collections?.has?.("unisex-perfumes") ||
              (p?.collections?.has?.("women") && p?.collections?.has?.("men")))
        );
      }
      return products.filter((p) => !this.isKidsProduct(p));
    }

    scoreCatalog(filters = {}) {
      let min = filters.minPrice;
      let max = filters.maxPrice;
      const preferred = new Set();
      if (filters.audience && AUDIENCE[filters.audience]) {
        AUDIENCE[filters.audience].handles.forEach((h) => preferred.add(h));
      }
      if (filters.brand && BRANDS[filters.brand]) {
        BRANDS[filters.brand].forEach((h) => preferred.add(h));
      }
      if (filters.category === "lighter" && CATEGORIES.lighter.handles) {
        CATEGORIES.lighter.handles.forEach((h) => preferred.add(h));
      }
      if (filters.audience === "gift" && !filters.category) {
        ["gift-sets", "bundles", "best-seller", "lighters", "zippo-lighters"].forEach((h) => preferred.add(h));
      }

      const vibeWords = filters.vibe ? [...(VIBES[filters.vibe] || [])] : [];
      const queryLower = String(filters.query || "").toLowerCase();
      const queryWords = queryLower
        .split(/[^a-z0-9+\u0600-\u06FF]+/i)
        .filter((w) => w.length > 2);

      // Lebanese / Arabizi audience hints — kids/girls/boys before women.
      if (!filters.audience) {
        if (
          /\bkids?\b|\bchildren\b|\bboys?\b|\bgirls?\b|\bdisney\b|\btubbees\b|للولاد|للاولاد|للأولاد|للطفل|للاطفال|للأطفال/.test(
            queryLower
          )
        ) {
          filters = { ...filters, audience: "kids" };
        } else if (
          /\bla\s*benet\b|\bbint\b|\bfor her\b|\bwomen\b|\bfemale\b|للبنت|للبنات|للمرأ|للنساء/.test(
            queryLower
          )
        ) {
          filters = { ...filters, audience: "women" };
        } else if (
          /\bla\s*shabb\b|\bzalame\b|\bfor him\b|\bmen\b|\bmale\b|للشب|للشباب|للرجل/.test(queryLower)
        ) {
          filters = { ...filters, audience: "men" };
        } else if (/\bfor me\b|\bunisex\b|\blal\s*hal\b/.test(queryLower)) {
          filters = { ...filters, audience: "unisex" };
        } else if (/\bgift\b|\bhdiyye\b|هدية|هديه/.test(queryLower)) {
          filters = { ...filters, audience: "gift" };
        }
      }

      if (!filters.note) {
        const detectedNote = this.extractRequestedNote(queryLower);
        if (detectedNote) filters = { ...filters, note: detectedNote };
      }

      const wantsLighter =
        filters.category === "lighter" ||
        queryWords.some((w) => ["lighter", "lighters", "zippo", "walla3a"].includes(w)) ||
        /ولاعة|ولاعه/.test(queryLower);
      const wantsPerfume =
        filters.category === "perfume" ||
        queryWords.some((w) => ["perfume", "fragrance", "scent", "oud", "cologne", "3otr", "3otor", "3itr"].includes(w)) ||
        /عطر|عطور/.test(queryLower);

      Object.entries(VIBES).forEach(([id, words]) => {
        if (words.some((w) => String(filters.query || "").toLowerCase().includes(w))) {
          if (!filters.vibe) vibeWords.push(...words);
          else if (id === filters.vibe) vibeWords.push(...words);
        }
      });

      // When a specific note is requested, do not let sibling vibe words (e.g. caramel)
      // substitute for that note in scoring.
      const noteAliases = filters.note ? NOTE_ALIASES[filters.note] || [filters.note] : [];
      const vibeWordsForScore = filters.note
        ? vibeWords.filter((w) => noteAliases.includes(w) || w === filters.note)
        : vibeWords;

      return this.catalog
        .map((product) => {
          let score = product.available ? 2 : -20;
          const lighter = this.isLighterProduct(product);
          const kids = this.isKidsProduct(product);

          if (wantsLighter && !lighter) score -= 25;
          if (wantsLighter && lighter) score += 22;
          if (wantsPerfume && !wantsLighter && lighter) score -= 20;

          if (min != null && product.price < min) score -= 12;
          if (max != null && product.price > max) score -= 18;

          if (filters.audience === "kids") {
            if (!kids) score -= 100;
            else score += 28;
          } else if (filters.audience === "women") {
            if (kids) score -= 100;
            else if (product.collections.has("women")) score += 22;
            else score -= 40;
          } else if (filters.audience === "men") {
            if (kids) score -= 100;
            else if (product.collections.has("men")) score += 22;
            else score -= 40;
          } else if (filters.audience === "unisex") {
            if (kids) score -= 100;
            else if (product.collections.has("unisex-perfumes")) score += 20;
            else score -= 10;
          } else {
            // Unknown / gift: keep kids out of the default adult pool.
            if (kids && !wantsLighter) score -= 60;
            else if (preferred.size) {
              score += [...product.collections].some((c) => preferred.has(c)) ? 16 : -1;
            }
          }

          if (filters.note) {
            const strength = this.noteMatchStrength(product, filters.note);
            if (!strength) score -= 100;
            else score += strength * 18;
          } else {
            vibeWordsForScore.forEach((word) => {
              if (product.text.includes(word)) score += 5;
            });
          }

          queryWords.forEach((word) => {
            const title = product.title.toLowerCase();
            if (title === word) score += 50;
            else if (title.includes(word)) score += 18;
            else if (product.handle.includes(word)) score += 14;
            else if (product.text.includes(word)) score += 4;
          });
          if (product.collections.has("best-seller")) score += 3;
          if (product.collections.has("our-favourite")) score += 2;
          return { product, score };
        })
        .filter((row) => {
          if (row.score <= 4) return false;
          if (filters.audience === "kids") return this.isKidsProduct(row.product);
          if (filters.audience === "women") {
            return row.product.collections.has("women") && !this.isKidsProduct(row.product);
          }
          if (filters.audience === "men") {
            return row.product.collections.has("men") && !this.isKidsProduct(row.product);
          }
          if (filters.audience === "unisex") {
            return (
              !this.isKidsProduct(row.product) &&
              (row.product.collections.has("unisex-perfumes") ||
                (row.product.collections.has("women") && row.product.collections.has("men")))
            );
          }
          if (filters.note && !this.productHasNote(row.product, filters.note)) return false;
          // Default adult pool: no kids perfume mixed in.
          if (!wantsLighter && this.isKidsProduct(row.product)) return false;
          return true;
        })
        .sort((a, b) => b.score - a.score)
        .map((row) => row.product);
    }

    formatMoney(price) {
      const value = Number(price) || 0;
      try {
        return new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: window.Shopify?.currency?.active || "USD",
        }).format(value);
      } catch {
        return `$${value.toFixed(2)}`;
      }
    }

    addUser(text, options = {}) {
      const { persist = true } = options;
      const row = document.createElement("div");
      row.className = "perfume-advisor__row perfume-advisor__row--user";
      const bubble = document.createElement("div");
      bubble.className = "perfume-advisor__bubble perfume-advisor__bubble--user";
      bubble.textContent = cleanText(text);
      row.appendChild(bubble);
      this.messages.appendChild(row);
      if (persist) {
        this.transcript.push({ role: "user", text: cleanText(text) });
        this.saveChat();
      }
      this.scrollToBottom();
    }

    addTyping() {
      const row = document.createElement("div");
      row.className = "perfume-advisor__row perfume-advisor__row--bot";
      row.innerHTML = `
        <div class="perfume-advisor__avatar" aria-hidden="true">Y</div>
        <div class="perfume-advisor__bubble perfume-advisor__bubble--bot perfume-advisor__bubble--thinking">
          <span class="perfume-advisor__thinking-label">Thinking</span>
          <span class="perfume-advisor__typing" aria-label="Thinking"><span></span><span></span><span></span></span>
        </div>`;
      this.messages.appendChild(row);
      this.scrollToBottom();
      return row;
    }

    addBot(text, choices = null, products = null, options = {}) {
      const { persist = true, moreProducts = [], detail = false } = options;
      if (products?.length) this.lastProducts = products;
      const row = document.createElement("div");
      row.className = "perfume-advisor__row perfume-advisor__row--bot";

      const avatar = document.createElement("div");
      avatar.className = "perfume-advisor__avatar";
      avatar.setAttribute("aria-hidden", "true");
      avatar.textContent = "Y";
      row.appendChild(avatar);

      const bubble = document.createElement("div");
      bubble.className = `perfume-advisor__bubble perfume-advisor__bubble--bot${detail ? " perfume-advisor__bubble--detail" : ""}`;

      const message = document.createElement("div");
      message.className = "perfume-advisor__text";
      message.textContent = cleanText(text);
      bubble.appendChild(message);

      if (products?.length) {
        const grid = document.createElement("div");
        grid.className = "perfume-advisor__products";
        products.forEach((product) =>
          grid.appendChild(detail ? this.renderDetailCard(product) : this.renderProduct(product))
        );
        bubble.appendChild(grid);

        const queue = [...(moreProducts || [])];
        this.moreQueue = queue;

        if (queue.length) {
          const moreBtn = document.createElement("button");
          moreBtn.type = "button";
          moreBtn.className = "perfume-advisor__more";
          moreBtn.textContent = "Show me more";
          moreBtn.addEventListener("click", () => {
            if (!queue.length) return;
            const next = queue.splice(0, PRODUCT_PAGE_SIZE);
            this.moreQueue = queue;
            next.forEach((product) => grid.appendChild(this.renderProduct(product)));

            if (persist) {
              const last = this.transcript[this.transcript.length - 1];
              if (last?.role === "bot") {
                last.products = [
                  ...(last.products || []),
                  ...this.snapshotProducts(next),
                ];
                this.saveChat();
              }
            }

            if (!queue.length) {
              moreBtn.remove();
            } else {
              moreBtn.textContent = `Show me more (${queue.length})`;
            }
            this.scrollToBottom();
          });
          bubble.appendChild(moreBtn);
        }
      }

      if (choices?.length) {
        const wrap = document.createElement("div");
        wrap.className = "perfume-advisor__suggestions";
        choices.forEach((choice) => {
          const button = document.createElement("button");
          button.type = "button";
          button.className = "perfume-advisor__suggestion";
          const label = cleanText(choice.label);
          button.textContent = label;
          button.addEventListener("click", () => {
            wrap.querySelectorAll("button").forEach((node) => {
              node.disabled = true;
            });
            this.send(label);
          });
          wrap.appendChild(button);
        });
        bubble.appendChild(wrap);
      }

      row.appendChild(bubble);
      this.messages.appendChild(row);

      if (persist) {
        this.transcript.push({
          role: "bot",
          text: cleanText(text),
          choices: choices?.length ? choices.map((c) => cleanText(c.label)) : [],
          products: products?.length ? this.snapshotProducts(products) : [],
          detail: Boolean(detail),
        });
        this.saveChat();
      }

      this.scrollToBottom();
      return bubble;
    }

    renderDetailCard(product) {
      const bits = this.detailBits(product);
      const card = document.createElement("article");
      card.className = "perfume-advisor__detail";

      const media = document.createElement("a");
      media.className = "perfume-advisor__detail-image";
      media.href = product.url;
      if (product.image) {
        const img = document.createElement("img");
        img.src = product.image;
        img.alt = product.title;
        img.loading = "lazy";
        media.appendChild(img);
      }
      card.appendChild(media);

      const body = document.createElement("div");
      body.className = "perfume-advisor__detail-body";

      const kicker = document.createElement("p");
      kicker.className = "perfume-advisor__detail-kicker";
      kicker.textContent = this.productKind(product);
      body.appendChild(kicker);

      const title = document.createElement("a");
      title.className = "perfume-advisor__detail-title";
      title.href = product.url;
      title.textContent = cleanText(product.title);
      body.appendChild(title);

      const price = document.createElement("p");
      price.className = "perfume-advisor__detail-price";
      const priceBits = [this.formatMoney(product.price)];
      if (product.vendor && product.vendor !== "My Store") priceBits.push(product.vendor);
      price.textContent = priceBits.join(" · ");
      body.appendChild(price);

      const stock = document.createElement("p");
      stock.className = `perfume-advisor__detail-stock${product.available === false ? " is-out" : ""}`;
      stock.textContent = product.available === false ? "Sold out" : "In stock";
      body.appendChild(stock);

      if (bits.about) {
        const about = document.createElement("p");
        about.className = "perfume-advisor__detail-about";
        about.textContent = bits.about;
        body.appendChild(about);
      }

      const notes = [
        ["Top", bits.top],
        ["Heart", bits.heart],
        ["Base", bits.base],
      ].filter((row) => row[1]);
      if (notes.length) {
        const list = document.createElement("ul");
        list.className = "perfume-advisor__detail-notes";
        notes.forEach(([label, value]) => {
          const item = document.createElement("li");
          const name = document.createElement("span");
          name.textContent = label;
          item.appendChild(name);
          item.appendChild(document.createTextNode(value));
          list.appendChild(item);
        });
        body.appendChild(list);
      }

      const actions = document.createElement("div");
      actions.className = "perfume-advisor__detail-actions";

      if (product.variantId && product.available !== false) {
        const add = document.createElement("button");
        add.type = "button";
        add.className = "perfume-advisor__add";
        add.textContent = "Add to cart";
        add.addEventListener("click", () => this.addProductToCart(product, add, false));
        actions.appendChild(add);

        const buy = document.createElement("button");
        buy.type = "button";
        buy.className = "perfume-advisor__buy";
        buy.textContent = "Buy now";
        buy.addEventListener("click", () => this.addProductToCart(product, buy, true));
        actions.appendChild(buy);
      } else {
        const view = document.createElement("a");
        view.href = product.url;
        view.className = "perfume-advisor__link";
        view.textContent = "View product";
        actions.appendChild(view);
      }

      body.appendChild(actions);
      card.appendChild(body);
      return card;
    }

    async addProductToCart(product, button, checkout) {
      if (!product?.variantId) return;
      const label = button.textContent;
      button.disabled = true;
      button.textContent = checkout ? "Opening…" : "Adding…";
      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: [{ id: product.variantId, quantity: 1 }] }),
        });
        if (!response.ok) throw new Error("cart");
        document.dispatchEvent(new CustomEvent("cart:refresh"));
        if (checkout) {
          const root = window.Shopify?.routes?.root || "/";
          window.location.href = `${root}checkout`;
          return;
        }
        button.textContent = "Added";
      } catch {
        button.textContent = label;
        button.disabled = false;
      }
    }

    renderProduct(product) {
      const card = document.createElement("article");
      card.className = "perfume-advisor__product";

      const media = document.createElement("a");
      media.className = "perfume-advisor__product-image";
      media.href = product.url;
      if (product.image) {
        const img = document.createElement("img");
        img.src = product.image;
        img.alt = product.title;
        img.loading = "lazy";
        media.appendChild(img);
      }
      card.appendChild(media);

      const body = document.createElement("div");
      body.className = "perfume-advisor__product-body";

      const title = document.createElement("a");
      title.className = "perfume-advisor__product-title";
      title.href = product.url;
      title.textContent = cleanText(product.title);
      body.appendChild(title);

      const meta = document.createElement("p");
      meta.className = "perfume-advisor__product-price";
      const bits = [this.formatMoney(product.price)];
      if (product.vendor && product.vendor !== "My Store") bits.unshift(product.vendor);
      meta.textContent = bits.join(" · ");
      body.appendChild(meta);

      const actions = document.createElement("div");
      actions.className = "perfume-advisor__product-actions";

      const view = document.createElement("a");
      view.href = product.url;
      view.className = "perfume-advisor__link";
      view.textContent = "View";
      actions.appendChild(view);

      if (product.variantId && product.available !== false) {
        const add = document.createElement("button");
        add.type = "button";
        add.className = "perfume-advisor__add";
        add.textContent = "Add to cart";
        add.addEventListener("click", async () => {
          add.disabled = true;
          add.textContent = "Adding…";
          try {
            await fetch("/cart/add.js", {
              method: "POST",
              headers: { "Content-Type": "application/json", Accept: "application/json" },
              body: JSON.stringify({ items: [{ id: product.variantId, quantity: 1 }] }),
            });
            add.textContent = "Added";
            document.dispatchEvent(new CustomEvent("cart:refresh"));
          } catch {
            add.textContent = "Add to cart";
            add.disabled = false;
          }
        });
        actions.appendChild(add);
      }

      body.appendChild(actions);
      card.appendChild(body);
      return card;
    }

    scrollToBottom() {
      requestAnimationFrame(() => {
        this.messages.scrollTop = this.messages.scrollHeight;
      });
    }
  }

  if (!customElements.get("perfume-advisor")) {
    customElements.define("perfume-advisor", PerfumeAdvisor);
  }
})();
